import { getClientBaseUrl } from "@client/trpc";

/**
 * Build the proxy URL for a video's active poster (thumbnail) image, served by
 * `/api/videos/poster/:videoId`.
 *
 * The URL is prefixed with `getClientBaseUrl()` so it loads everywhere the
 * client runs: empty (relative) in browsers and dev-server native builds, the
 * deployed API host in bundled native builds. The bundled iPad/iPhone WebView's
 * origin is `capacitor://localhost`, so a bare relative path would resolve
 * against the local app bundle and silently fall back to the placeholder
 * (SP-2317) — callers must never prepend their own base on top of this URL.
 *
 * The active poster id is included as a `?v=` version param: the proxy caches
 * responses (max-age), and the bare URL is identical no matter which candidate
 * is active, so without a version an admin switching the active thumbnail
 * wouldn't show for already-cached viewers until the cache expired. Keying the
 * URL on the active poster id changes the URL the instant the selection
 * changes, so the new thumbnail is fetched immediately.
 *
 * Returns null when the video has no active poster (activePosterId null), which
 * callers use to fall back to a placeholder.
 *
 * The `r=` revision alongside it is NOT part of the poster's identity — see
 * POSTER_URL_REVISION. The server ignores it; it exists purely so we can
 * abandon stored responses when we have to.
 */
/**
 * Cache-key generation for poster URLs. Bump this to make every browser
 * abandon what it has stored for every poster and fetch again.
 *
 * It exists because a stored response is unreachable from the server: no
 * header on any future request can evict an entry, since a browser holding a
 * fresh answer never makes the request. The cache key is the only lever, and
 * changing it is the only way to repair clients that already hold a bad entry.
 *
 * Revision 2 retires the entries poisoned by the poster proxy's error
 * responses, which inherited `immutable, max-age=1y` from the success path and
 * so pinned transient 500s in place for a year — invisible to the server,
 * because the browser stopped asking, and unable to expire on their own,
 * because a poster that never changes has a URL that never changes. The cause
 * is fixed (`server/lib/neverCacheErrorResponses.ts`); this repairs the
 * clients that were hit before it landed.
 *
 * ONLY bump this to invalidate poisoned entries. It is not a versioning
 * mechanism for poster CONTENT — a new thumbnail already changes
 * `activePosterId`, and therefore the URL, by itself. Each bump costs every
 * viewer one refetch of every poster they see, so it buys nothing when the
 * stored responses are healthy.
 */
const POSTER_URL_REVISION = 2;

export function videoPosterUrl(
  videoId: string | null | undefined,
  activePosterId: string | null | undefined,
): string | null {
  if (!videoId || !activePosterId) {
    return null;
  }

  return `${getClientBaseUrl()}/api/videos/poster/${encodeURIComponent(videoId)}?v=${encodeURIComponent(activePosterId)}&r=${POSTER_URL_REVISION}`;
}

/**
 * The poster sources to try, in order, for a `<VideoPoster>`.
 *
 * `posterCdnUrl` is the SERVER's decision (see `server/lib/posterCdnUrl.ts`) and
 * arrives in the same payload that carries the poster id. Null means "no CDN URL
 * applies" — a legacy object layout, a preview, dev, or no CDN host configured —
 * and every one of those cases is normal, not an error.
 *
 * Both are returned rather than just the winner because the CDN URL can fail in
 * ways the proxy will not: a stale poster id (an admin regenerated thumbnails
 * while this page was open) 404s at the bucket, where the proxy instead degrades
 * to the video's CURRENT active poster; and a CDN outage or a CORS
 * misconfiguration would take out every thumbnail at once. So the proxy stays
 * behind it as a retry (6.3), which is possible for images and not for JavaScript
 * — an `<img>` has an error hook, a module import does not.
 *
 * The client never inspects, parses or builds the CDN URL. It cannot: whether one
 * applies depends on the poster's stored object path, which only the server reads
 * (6.1).
 */
export function videoPosterSources(
  videoId: string | null | undefined,
  activePosterId: string | null | undefined,
  posterCdnUrl: string | null | undefined,
): readonly string[] {
  const proxyUrl = videoPosterUrl(videoId, activePosterId);

  if (proxyUrl == null) {
    return [];
  }

  return posterCdnUrl == null || posterCdnUrl === ""
    ? [proxyUrl]
    : [posterCdnUrl, proxyUrl];
}

/**
 * The poster URL for the `<video poster>` attribute: the server's CDN URL when
 * one applies, the versioned proxy otherwise.
 *
 * Lives HERE rather than beside `useVideoSource`, which is its only real caller,
 * because the code-split shell (`VideoPlayerLazy`) needs it to paint a poster
 * before the player chunk loads — and that shell is in the eagerly-loaded barrel.
 * Importing it from the hook's module put `useVideoSource` and `useLocalVideoSrc`
 * into the eager graph of every page that renders any poster UI, measured on a
 * real build. This module is pure and dependency-light, which is what keeps that
 * from happening again.
 *
 * When the caller already knows the active poster, this paints while the player
 * bundle and stream URL load. Callers that only know `hasPoster` get it once the
 * media payload lands (useVideoSource feeds that payload's poster fields in), so
 * both render phases resolve to the SAME URL and the browser fetches one poster.
 * Until a poster is known there is deliberately no poster — the payload no
 * longer carries a signed GCS poster URL to fall back to.
 *
 * `posterId` and `posterCdnUrl` are ONE value in two parts and must come from the
 * same payload. Mixing an eager id with a payload-derived CDN URL is what makes
 * the image download twice.
 *
 * NO FALLBACK HERE, unlike the thumbnails. A `<video poster>` attribute has no
 * error hook, so a CDN URL that 404s means no frame before playback rather than a
 * retry (6.3). Degraded, not broken — and the same failure recovers visibly in
 * any VideoPoster on the page.
 */
export function resolveVideoPosterUrl({
  videoId,
  hasPoster,
  posterId,
  posterCdnUrl,
}: {
  videoId: string;
  hasPoster: boolean;
  posterId?: string | null;
  posterCdnUrl?: string | null;
}): string | undefined {
  if (!hasPoster) {
    return undefined;
  }

  const proxyUrl = videoPosterUrl(videoId, posterId);

  if (proxyUrl == null) {
    return undefined;
  }

  return posterCdnUrl == null || posterCdnUrl === "" ? proxyUrl : posterCdnUrl;
}
